import { AsyncPipe, NgClass } from '@angular/common';
import { Component, HostListener, inject, Input, ViewChild } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@rms/auth';
import { BalanceWidgetComponent } from '@rms/billing';
import { WhiteLabelService } from '@rms/core/layout';
import { Organization, OrganizationStoreService, Sections } from '@rms/org';
import { DestroyService } from '@rms/sdk/destroy';
import { UserInitialsPipe } from '@rms/sdk/pipes';
import { UserStoreService } from '@rms/user';
import { VexScrollbarComponent } from '@vex/components/vex-scrollbar/vex-scrollbar.component';
import { VexConfigService } from '@vex/config/vex-config.service';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, takeUntil, tap } from 'rxjs/operators';

import { NavigationService } from '../../../core/navigation/navigation.service';
import { NavigationItem } from '../../../core/navigation/navigation-item.interface';
import { SearchModalComponent } from './search-modal/search-modal.component';
import { SidenavItemComponent } from './sidenav-item/sidenav-item.component';

@Component({
  selector: 'rms-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  imports: [
    MatBadgeModule,
    MatButtonModule,
    MatIconModule,
    MatRippleModule,
    MatTooltipModule,
    VexScrollbarComponent,
    SidenavItemComponent,
    BalanceWidgetComponent,
    AsyncPipe,
    TranslateModule,
    UserInitialsPipe,
    NgClass,
    MatMenuModule,
    RouterModule,
  ],
  providers: [DestroyService],
})
export class SidenavComponent {
  @Input() public collapsed = false;
  @ViewChild('userMenu') public userMenu?: MatMenu;
  @HostListener('window:resize')
  public onResize(): void {
    if (this.userMenu) {
      this.userMenu.closed.emit();
    }
  }

  private readonly navigationService = inject(NavigationService);
  private readonly layoutService = inject(VexLayoutService);
  private readonly configService = inject(VexConfigService);
  private readonly userStore = inject(UserStoreService);
  private readonly organizationStore = inject(OrganizationStoreService);
  private readonly dialog = inject(MatDialog);
  private readonly whiteLabelService = inject(WhiteLabelService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly destroy$ = inject(DestroyService);

  public collapsedOpen$ = this.layoutService.sidenavCollapsedOpen$;
  public imageUrl$ = this.configService.config$.pipe(map((config) => config.sidenav.imageUrl));
  public collapseImageUrl$ = this.configService.config$.pipe(map((config) => config.sidenav.collapseImageUrl));
  public userVisible$ = this.configService.config$.pipe(map((config) => config.sidenav.user.visible));
  public searchVisible$ = this.configService.config$.pipe(map((config) => config.sidenav.search.visible));
  public userName$ = this.userStore.user$.pipe(map((user) => user.full_name));
  public isDesktop$ = this.layoutService.isDesktop$;

  public org$ = this.organizationStore.organization$.pipe(
    filter(Boolean),
    map((org: Organization) => org.name)
  );

  public isLocked$ = this.organizationStore.isLocked$;

  public logoUrl$ = combineLatest([this.imageUrl$, this.collapseImageUrl$, this.layoutService.sidenavCollapsed$]).pipe(
    map(([logo, collapseLogo, collapsed]) => {
      return collapsed ? collapseLogo : logo;
    })
  );

  public userMenuOpen$: Observable<boolean> = of(false);

  public items$: Observable<NavigationItem[]> = this.navigationService.items$;

  public isLogoEnabled$ = this.whiteLabelService.isLogoEnabled$;
  public hideCompany$ = this.whiteLabelService.isMenuCompanyHidden$;
  public isHelpCenterEnabled$ = this.whiteLabelService.isHelpCenterEnabled$;

  /*
   * Note: Currently, logout is the only element in the popover for the user card.
   * If there are more, this logic will need to be moved to the rms-sidenav-user-menu component.
   *  */
  public isLogoutEnabled$ = this.whiteLabelService.isLogoutEnabled$;

  public balanceError = false;
  public isSettingsEnabled = this.whiteLabelService.isMenuItemAvailable(Sections.Settings);
  public showMinuteBundles = true;

  private title$ = this.configService.config$.pipe(map((config) => config.sidenav.title));

  public collapseOpenSidenav(): void {
    this.layoutService.collapseOpenSidenav();
  }

  public collapseCloseSidenav(): void {
    this.layoutService.collapseCloseSidenav();
  }

  public toggleCollapse(): void {
    this.collapsed ? this.layoutService.expandSidenav() : this.layoutService.collapseSidenav();
  }

  public trackByRoute(_: number, item: NavigationItem): string {
    if (item.type === 'link') {
      return item.route;
    }

    return item.label;
  }

  public logout(): void {
    this.authService
      .logout()
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null)),
        tap(() => this.router.navigate(['/login']))
      )
      .subscribe();
  }

  public openSearch(): void {
    this.dialog.open(SearchModalComponent, {
      panelClass: 'vex-dialog-glossy',
      width: '100%',
      maxWidth: '600px',
    });
  }

  public navigateToSettings(): void {
    if (!this.isSettingsEnabled) {
      return;
    }

    if (this.balanceError) {
      return;
    }

    this.router.navigate(['../settings/billing']);
  }
}
